<template>
  <div class="StageOnboardingForm">
    <div v-if="!isSelectedCountryEnabled" class="mb-32">
      <CardWrapper type-class="card--warning">
        <template #body>
          {{ $t('we_dont_provide_service_in_country') }}
          <strong>{{ country.local_title }}</strong
          >.
          {{ $t('rest_assured') }}
        </template>
      </CardWrapper>
    </div>
    <div v-if="leadSent" class="mb-32">
      <CardWrapper type-class="card--success">
        <template #body>
          {{ $t('we_have_received_your_information') }}
          <strong>{{ country.local_title }}</strong
          >.
        </template>
      </CardWrapper>
    </div>
    <div v-if="error.title" class="mb-32">
      <CardWrapper type-class="card--error" class="my-16">
        <template #body>
          <h3>
            {{ error.title }}
          </h3>
          <div v-if="error.description">
            {{ error.description }}
          </div>
        </template>
      </CardWrapper>
    </div>
    <div class="flex flex-col items-center">
      <template v-if="countries.length">
        <SearchAutocomplete
          id="search-autocomplete"
          class="mb-24"
          :items="countries"
          title-prop-name="title"
          left-icon-class="icon-planet-earth"
          :label="$t('country')"
          :initial-value="initial_stage_country_name"
          @select-option="setAutocompleteResult"
        />
      </template>
      <template v-if="isSelectedCountryEnabled">
        <StageNameAndSlugForm
          :loading="loading"
          :is-country-selected="!!country"
          :is-onboarding-stage-creation="isOnboardingStageCreation"
          @on-send-stage-data="sendStageForm"
        />
      </template>
      <template v-else>
        <LeadForm :is-loading="loading" @send-lead="sendLead" />
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import stagesServices from '@/api/services/stagesServices.js'
import StageNameAndSlugForm from '@/components/forms/StageNameAndSlugForm.vue'
import LeadForm from '@/components/forms/LeadForm.vue'
import { CardWrapper } from '@snowshoe-frontend/shared-components'
import { SearchAutocomplete } from '@snowshoe-frontend/form-fields-components'
import { storeToRefs } from 'pinia'
import { useAuthStore } from '@/stores/authStore'
import { useCommonStore } from '@/stores/commonStore'
import { useErrorsStore } from '@/stores/errorsStore'
import { useOnboardingStore } from '@/stores/onboardingStore'
import { useUserStore } from '@/stores/userStore'
import { useLanguages } from '@/composables/useLanguages'
import { useNotificationsStore } from '@/stores/notificationsStore'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  isOnboardingStageCreation: {
    type: Boolean,
    default: false
  }
})

const router = useRouter()
const { getIriByLocale } = useLanguages()

const { setNotification } = useNotificationsStore()
const authStore = useAuthStore()
const commonStore = useCommonStore()
const errorsStore = useErrorsStore()
const onboardingStore = useOnboardingStore()
const userStore = useUserStore()

const { authData } = storeToRefs(authStore)
const { countries } = storeToRefs(commonStore)
const { error } = storeToRefs(errorsStore)
const { initial_stage_country_name } = storeToRefs(onboardingStore)
const { user } = storeToRefs(userStore)
const { t } = useI18n()

const country = ref(undefined)
const countryName = ref('')
const isSelectedCountryEnabled = ref(true)
const loading = ref(false)
const leadSent = ref(false)

function setAutocompleteResult(countrySelected) {
  const countryObj = countries.value.find(
    (country) => country.title.toLowerCase() === countrySelected.toLowerCase()
  )
  country.value = countryObj
  countryName.value = countrySelected

  if (countrySelected) {
    isSelectedCountryEnabled.value = countryObj.enabled
  } else {
    isSelectedCountryEnabled.value = true
  }
}

async function sendLead(payload) {
  try {
    loading.value = true

    if (props.isOnboardingStageCreation) {
      const body = {
        ...payload,
        languageId: getIriByLocale(payload.language),
        countryId: country.value.id
      }
      await commonStore.sendLeadForm(body)
    } else {
      const authUserInfo = {
        languageId: getIriByLocale(user.value.language),
        firstName: user.value.first_name,
        lastName: user.value.last_name,
        email: user.value.email
      }

      const body = { ...authUserInfo, countryId: country.value.id }
      await commonStore.sendLeadForm(body)
    }

    leadSent.value = true
    isSelectedCountryEnabled.value = true
  } catch (e) {
    setNotification({
      header: e.message,
      body: 'An error occurred',
      type: 'danger'
    })
  } finally {
    loading.value = false
  }
}

async function sendStageForm(payload) {
  const { initial_stage_name, initial_stage_slug } = payload

  try {
    loading.value = true

    if (props.isOnboardingStageCreation) {
      onboardingStore.saveStageFormData({
        initial_stage_country_name: countryName.value,
        initial_stage_country: country.value.id,
        initial_stage_name,
        initial_stage_slug
      })
      await router.push({ name: 'AccountOnboarding' })
    } else {
      await stagesServices.createStage({
        token: authData.value.token,
        newStagePayload: {
          title: initial_stage_name,
          fqdn: initial_stage_slug,
          country: country.value.id,
          language: user.value.language
        }
      })

      await router.push({ name: 'WelcomeView' })
    }
  } catch (e) {
    setNotification({
      header: t('account_common_error'),
      body: 'Whoops! It seems we have problems with changing your form.',
      type: 'danger'
    })
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  try {
    if (countries.value.length) return

    await commonStore.getCountries()
  } catch (e) {
    setNotification({
      header: e.message,
      body: 'An error occurred',
      type: 'danger'
    })
  }
})
</script>
